import { useNavigate } from 'react-router-dom';
import { ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getUser } from '../../utils/tokens';
import axios from 'axios';
import CategoryRow from './CategoryRow';
import { useAuthDispatch, useAuthState } from '../../contexts/auth';
import {
	closestCenter,
	DndContext,
	DragOverlay,
	KeyboardSensor,
	MeasuringStrategy,
	MouseSensor,
	rectIntersection,
	TouchSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
	arrayMove,
	defaultAnimateLayoutChanges,
	SortableContext,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { filter, find, forEach, prop, sortBy } from 'rambda';
import CategoryRowItem from './CategoryRowItem';

function classNames(...classes) {
	return classes.filter(Boolean).join(' ');
}

const listUrl = clientId => `resource/v1/resource-categories/clients/${clientId}`;
const sortUrl = (categoryId, idx) => `resource/v1/resource-categories/sort/${categoryId}/position/${idx}`;

const CategoriesPage = ({ categories, setCategories }) => {
	const [globals, setGlobals] = useState(null);
	const [activeId, setActiveId] = useState(null);
	const [activeCat, setActiveCat] = useState(null);
	const lastOverId = useRef(null);
	const recentlyMovedToNewContainer = useRef(false);

	const isSortingContainer = ({ id }) => {
		if (!id) {
			return false;
		}

		const parts = id.split(':');
		return parts.length === 2;
	};

	const getParentId = ({ id }) => {
		if (!id) {
			return false;
		}

		const parts = id.split(':');
		return parts[1];
	};

	const getCategorySortId = ({ id }) => {
		if (!id) {
			return false;
		}

		const parts = id.split(':');
		return parts.length === 2 ? parts[1] : parts[2];
	};

	const getCategoryForId = ({ id }) => {
		if (!id) {
			return {};
		}

		const parts = id.split(':');
		const parentCat = find(c => parts[1] === c.id.toString(), categories);
		if (parts.length === 2) {
			return parentCat;
		} else {
			return find(c => parts[2] === c.idx.toString(), parentCat.children);
		}
	};

	const { inFrame } = useAuthState();
	let navigate = useNavigate();
	const auth = useAuthDispatch();

	useEffect(categoriesEffect, [inFrame]);

	useEffect(() => {}, [categories]);

	function categoriesEffect() {}

	function handleAddClick() {
		navigate('create');
	}

	function handleBack() {
		navigate(-1);
	}

	const mouseSensor = useSensor(MouseSensor);
	const touchSensor = useSensor(TouchSensor);
	const keyboardSensor = useSensor(KeyboardSensor);
	const PLACEHOLDER_ID = 'placeholder';

	const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor);

	const collisionDetectionStrategy = useCallback(
		args => {
			// Start by finding any intersecting droppable
			let overId = rectIntersection(args);
			const activeIdParts = activeId.split(':');
			if (activeId && activeIdParts.length === 2) {
				const dcs = filter(dc => {
					const parts = dc.id.split(':');
					return parts.length === 2;
				}, args.droppableContainers);

				return closestCenter({
					...args,
					droppableContainers: dcs,
				});
			}

			if (overId != null) {
				if (isSortingContainer({ id: activeId })) {
					const sortableContainer = filter(oid => {
						const parts = oid.split(':');
						return parts.length === 2;
					}, overId);
				}

				const id = overId[0].id;
				const overIdParts = id.split(':');
				if (overIdParts.length === 2) {
					const container = find(c => overIdParts[1] === c.id.toString(), categories);
					const containerItems = container.children;
					// If a container is matched and it contains children
					if (containerItems.length > 0) {
						// Return the closest droppable within that container
						overId = closestCenter({
							...args,
							droppableContainers: args.droppableContainers.filter(
								container => container.id !== overId && containerItems.includes(container.id),
							),
						});
					}
				}

				lastOverId.current = overId;

				return overId;
			}

			// When a draggable item moves to a new container, the layout may shift
			// and the `overId` may become `null`. We manually set the cached `lastOverId`
			// to the id of the draggable item that was moved to the new container, otherwise
			// the previous `overId` will be returned which can cause items to incorrectly shift positions
			if (recentlyMovedToNewContainer.current) {
				lastOverId.current = activeId;
			}

			// If no droppable is matched, return the last match
			return lastOverId.current;
		},
		[activeId, categories],
	);

	const handleDragEnd = ({ active, over }) => {
		const overId = over?.id;
		const overParts = overId.split(':');
		const activeParts = active.id.split(':');
		let newParentCategoryId;

		const data = {
			method: 'PATCH',
		};

		if (
			(!isSortingContainer({ id: activeId }) && isSortingContainer({ id: overId })) ||
			(isSortingContainer({ id: activeId }) && !isSortingContainer({ id: overId }))
		) {
			setActiveId(null);
			setActiveCat(null);
			return;
		} else if (isSortingContainer({ id: activeId }) && isSortingContainer({ id: overId })) {
			const oldIdx = categories.findIndex(item => item.id.toString() === activeParts[1]);
			const newIdx = categories.findIndex(item => item.id.toString() === overParts[1]);
			setCategories(categories => {
				return arrayMove(categories, oldIdx, newIdx);
			});

			data.url = sortUrl(activeParts[1], newIdx);
			setCategories(cats => {
				return arrayMove(cats, oldIdx, newIdx);
			});
		} else if (getParentId({ id: activeId }) !== getParentId({ id: overId })) {
			newParentCategoryId = getParentId({ id: overId });
			data.url =
				sortUrl(activeCat.id, getCategorySortId({ id: overId })) + '?parent-category=' + newParentCategoryId;
		} else {
			data.url = sortUrl(activeCat.id, getCategorySortId({ id: overId }));
		}

		if (!overId) {
			setActiveId(null);
			setActiveCat(null);
			return;
		}

		if (active.id !== over.id) {
			axios(data).then(
				resp => {
					sortCategories({ cats: resp.data, setCategories: setCategories });
				},
				error => {
					console.log(error);
				},
			);
		}

		setActiveCat(null);
		setActiveId(null);
	};

	const handleDragOver = ({ active, over }) => {
		const overId = over?.id;

		if (!overId || isSortingContainer({ id: activeId })) {
			return;
		}

		const activeIdParts = active.id.split(':');

		// inner layers droppable
		const overContainer = findContainer(overId);
		const activeContainer = findContainer(active.id);

		if (!overContainer || !activeContainer) {
			return;
		}

		if (activeContainer.id !== overContainer.id) {
			setCategories(cats => {
				const activeItems = activeContainer.children;
				const overItems = overContainer.children;
				const overIndex = 1;

				if (!isSortingContainer({ id: activeId })) {
					activeCat.parentCategoryId = overContainer.id;
				}

				let newIndex;

				if (isSortingContainer({ id: overId })) {
					newIndex = overItems.length + 1;
				} else {
					const isBelowOverItem =
						over &&
						active.rect.current.translated &&
						active.rect.current.translated.offsetTop > over.rect.offsetTop + over.rect.height;
				}

				recentlyMovedToNewContainer.current = true;

				// remove active.id from active container
				activeContainer.children = filter(c => {
					return activeIdParts[2] !== c.idx.toString();
				}, activeItems);
				overContainer.children.unshift(activeCat);

				return cats;
			});
		}
	};

	const findContainer = id => {
		const idParts = id.split(':');

		let container = find(c => {
			return c.id.toString() === idParts[1];
		}, categories);

		// if (idParts.length > 2) {
		// 	container = find(c => {
		// 		return c.idx.toString() === idParts[2];
		// 	}, container.children);
		// }

		return container;
	};

	function renderRowOverlay(id) {
		const parts = id.split(':');
		const activeContainer = find(c => parts[1] === c.id.toString(), categories);
		const activeCat = find(c => parts[2] === c.idx.toString(), activeContainer.children);

		return (
			<div>
				<div
					className={classNames(
						activeCat.parentCategoryId ? 'px-8 text-gray-500' : 'px-4',
						'group flex items-center px-4 py-4',
					)}>
					<div className="flex min-w-0 flex-1 items-center text-sm font-medium">
						<div className="flex-shrink-0">{activeCat.title}</div>
					</div>
				</div>
			</div>
		);
	}

	function renderContainerOverlay(id) {
		const parts = id.split(':');
		let activeCat = find(c => parts[1] === c.id.toString(), categories);
		if (parts.length === 3) {
			activeCat = find(c => parts[2] === c.idx.toString(), activeCat.children);
		}

		return (
			<div className="bg-gray-50 divide-y divide-gray-200">
				<div className={classNames('px-4 group flex items-center px-4 py-4')}>
					<div className="flex min-w-0 flex-1 items-center text-sm font-medium">
						<div className="flex-shrink-0">{activeCat.title}</div>
					</div>
					<div className="flex min-w-0 flex-1 items-center text-sm font-medium text-gray-900">
						<p className="truncate text-sm font-medium"></p>
					</div>
					<div className="flex min-w-0 flex-1 items-center text-sm font-medium text-gray-400"></div>
					<div></div>
				</div>
				{activeCat.children.map(subCat => renderRowOverlay(id + ':' + subCat.idx))}
			</div>
		);
	}

	return (
		<div className="py-4 px-4 sm:px-6 lg:px-8">
			{!auth.isIframe() && (
				<div className="pb-4" onClick={handleBack}>
					<span className="text-indigo-600">
						<ArrowUturnLeftIcon className="h-6 w-6 text-indigo-600" />
					</span>
				</div>
			)}

			<div className="sm:flex sm:items-center">
				<div className="sm:flex-auto">
					<h1 className="text-xl font-semi-bold text-gray-900">Categories</h1>
				</div>
				<div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
					<button
						onClick={() => {
							handleAddClick();
						}}
						type="button"
						className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto">
						Add Category
					</button>
				</div>
			</div>

			<DndContext
				modifiers={[restrictToVerticalAxis]}
				collisionDetection={collisionDetectionStrategy}
				measuring={{
					droppable: {
						strategy: MeasuringStrategy.Always,
					},
				}}
				onDragStart={({ active }) => {
					setActiveId(active.id);
					setActiveCat(getCategoryForId({ id: active.id }));
				}}
				onDragOver={handleDragOver}
				onDragEnd={handleDragEnd}>
				<div className="-mx-4 mt-8 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:-mx-6 md:mx-0 md:rounded-lg">
					<div className="min-w-full divide-y divide-gray-300">
						<div className="bg-gray-200">
							<div className="flex">
								<div
									scope="col"
									className="flex min-w-0 flex-1 py-3.5 pl-4 pr-3 text-left text-sm font-semi-bold text-gray-900 sm:pl-6">
									Title
								</div>
								<div
									scope="col"
									className="flex min-w-0 flex-1 items-center px-3 py-3.5 text-left text-sm font-semi-bold text-gray-900 lg:table-cell w-24"></div>
								<div scope="col" className="w-24"></div>
							</div>
						</div>
						<div role="list" className="divide-y divide-gray-200 bg-white">
							<SortableContext
								items={categories.map(item => 'p:' + item.idx.toString())}
								sensors={sensors}
								strategy={verticalListSortingStrategy}>
								{categories.map(category => (
									<DroppableCatContainer
										key={category.id}
										category={category}
										categories={categories}
										setCategories={setCategories}>
										<SortableContext
											items={category.children.map(
												item => 'p:' + category.id + ':' + item.idx.toString(),
											)}
											sensors={sensors}
											strategy={verticalListSortingStrategy}>
											{category.children.map(subCat => (
												<>
													<CategoryRow
														key={subCat.id}
														category={subCat}
														sortId={'p:' + category.id + ':' + subCat.idx.toString()}
														categories={categories}
													/>
												</>
											))}
										</SortableContext>
									</DroppableCatContainer>
								))}
							</SortableContext>
						</div>
					</div>
				</div>

				<DragOverlay>
					{activeId
						? isSortingContainer({ id: activeId })
							? renderContainerOverlay(activeId)
							: renderRowOverlay(activeId)
						: null}
				</DragOverlay>
			</DndContext>
		</div>
	);
};

const LoadingCategoryList = () => {
	return <div>Loading category</div>;
};

const Categories = () => {
	const [categories, setCategories] = useState([]);
	const currentUser = getUser();

	useEffect(() => {
		const getCategories = async () => {
			return axios
				.get(listUrl(currentUser.clientIdNum))
				.then(response => {
					return response.data;
				})
				.catch(error => {
					console.log(error);
				});
		};

		getCategories().then(cats => {
			sortCategories({ cats: cats, setCategories: setCategories });
		});
	}, [currentUser.clientIdNum]);

	if (!categories) {
		return <LoadingCategoryList />;
	}

	if (categories) {
		return <CategoriesPage categories={categories} setCategories={setCategories} />;
	}
};

const sortCategories = ({ cats, setCategories }) => {
	if (cats) {
		const subCats = filter(c => {
			return c.parentCategoryId;
		}, cats);

		const topCats = filter(c => {
			return !c.parentCategoryId;
		}, cats);

		forEach(c => {
			c.children = filter(child => {
				if (child.parentCategoryId) {
					return child.parentCategoryId === c.id;
				}

				return false;
			}, subCats);

			if (c.parentCategoryId) {
				const parent = find(c2 => {
					return c2.id === c.parentCategoryId;
				}, cats);

				c.parentTitle = parent ? parent.title : '';
			}
		}, topCats);

		const sortedCats = sortBy(prop('idx'), topCats);
		forEach(c => {
			if (c.children) {
				c.children = sortBy(prop('idx'), c.children);
			}
		}, sortedCats);

		setCategories(sortedCats);
	}
};

const animateLayoutChanges = args => (args.isSorting || args.wasDragging ? defaultAnimateLayoutChanges(args) : true);

const DroppableCatContainer = ({ category, categories, setCategories, children }) => {
	let navigate = useNavigate();
	const { active, attributes, isDragging, listeners, over, setNodeRef, transition, transform } = useSortable({
		id: 'p:' + category.id,
		data: {
			type: 'container',
		},
		animateLayoutChanges,
	});

	const [title, setTitle] = useState(null);
	const [enabled, setEnabled] = useState(category.enabled);
	const [parentTitle, setParentTitle] = useState(category.parentTitle);
	const sortId = 'p:' + category.id;

	useEffect(() => {
		setTitle(category.title);
		setEnabled(category.enabled);
		setParentTitle(category.parentTitle);
	}, []);

	function isOverContainer() {
		return over ? (sortId === over.id && active?.data.current?.type !== 'container') || hasChild(over.id) : false;
	}

	function hasChild(id) {
		return find(c => {
			return c.id === id;
		}, category.children);
	}

	function handleEditClick(id) {
		navigate('edit/' + id);
	}

	return (
		<>
			<CategoryRowItem
				attributes={attributes}
				category={category}
				title={title}
				enabled={enabled}
				localCategory={category}
				parentTitle={parentTitle}
				listeners={listeners}
				nodeRef={setNodeRef}
				transition={transition}
				transform={transform}
				handleClick={handleEditClick}
			/>
			{children}
		</>
	);
};

export default Categories;
